import React, { useEffect, useRef } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { siteContent } from '../content/siteContent';

const FeaturedWork = () => {
  const sectionRef = useRef(null);
  const { featuredWork } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="work" ref={sectionRef} className="relative py-32 overflow-hidden bg-gradient-to-b from-transparent via-[#D22527]/5 to-transparent">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#D22527] rounded-full filter blur-3xl opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <div className="fade-in-up inline-block px-4 py-2 rounded-full bg-[#D22527]/10 border border-[#D22527]/30 text-[#D22527] text-sm font-semibold mb-6">
            {featuredWork.badge}
          </div>
          <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
            {featuredWork.heading[0]}{' '}
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {featuredWork.heading[1]}
            </span>
          </h2>
          <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{featuredWork.intro}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {featuredWork.proofHighlights.map((item, index) => (
            <div
              key={item.label}
              className="fade-in-up p-6 rounded-3xl bg-gradient-to-br from-[#D22527]/10 to-transparent border border-[#D22527]/25 backdrop-blur-sm"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#D22527] mb-3">{item.label}</p>
              <p className="text-3xl font-bold text-white mb-2">{item.value}</p>
              <p className="text-sm text-gray-400 leading-relaxed">{item.note}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredWork.projects.map((project, index) => (
            <div
              key={index}
              className="fade-in-up group relative p-8 rounded-3xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#D22527]/10 to-[#88171A]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10">
                <div className="inline-block px-3 py-1 rounded-full bg-[#D22527]/20 text-[#D22527] text-xs font-semibold mb-4">
                  {project.category}
                </div>

                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D22527] transition-colors duration-300">
                  {project.title}
                </h3>

                <p className="text-gray-400 mb-6 leading-relaxed">{project.description}</p>

                <div className="flex items-start gap-3 mb-6 p-4 rounded-2xl bg-black/40 border border-[#D22527]/15">
                  <CheckCircle2 className="w-5 h-5 text-[#D22527] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-1">Outcome Focus</p>
                    <p className="text-sm text-gray-300 leading-relaxed">{project.outcome}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 rounded-full bg-black/50 border border-[#D22527]/20 text-gray-400 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="text-sm text-white/85 font-semibold mb-6">
                  Proof Point: <span className="text-[#D22527]">{project.proof}</span>
                </div>

                <div className="flex items-center gap-2 text-[#D22527] group-hover:gap-4 transition-all duration-300">
                  <span className="text-sm font-semibold">{featuredWork.linkLabel}</span>
                  <ExternalLink className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="fade-in-up mt-14 text-center max-w-4xl mx-auto">
          <p className="text-sm uppercase tracking-[0.25em] text-[#D22527] mb-4">{featuredWork.closingLabel}</p>
          <p className="text-xl text-gray-300 leading-relaxed">{featuredWork.closingText}</p>
        </div>
      </div>
    </section>
  );
};

export default FeaturedWork;
